'use client'

import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { CheckCircle, Phone, Mail, MapPin, Clock, Banknote, ArrowLeftRight, CreditCard } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import { contactTranslations } from '@/translations/contact'

export default function Contact() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  const { language } = useLanguage()
  const t = contactTranslations[language]

  const contactInfo = [
    {
      icon: Phone,
      title: language === 'en' ? 'Call Me' : 'Ffoniwch Fi',
      value: t.contactInfo.callUs.number,
      link: `tel:${t.contactInfo.callUs.number}`,
      description: t.contactInfo.callUs.availability
    },
    {
      icon: Mail,
      title: language === 'en' ? 'Email Me' : 'E-bostiwch Fi',
      value: t.contactInfo.emailUs.email,
      link: `mailto:${t.contactInfo.emailUs.email}`,
      description: t.contactInfo.emailUs.response
    },
    {
      icon: MapPin,
      title: t.contactInfo.serviceArea.title,
      value: language === 'en' ? 'Barry and surrounding areas' : "Y Barri a'r ardaloedd cyfagos",
      link: `https://maps.google.com/?q=${encodeURIComponent(language === 'en' ? 'Barry and surrounding areas' : "Y Barri a'r ardaloedd cyfagos")}`,
      description: t.contactInfo.serviceArea.note
    },
    {
      icon: Clock,
      title: t.contactInfo.businessHours.title,
      value: t.contactInfo.businessHours.weekdays,
      link: null,
      description: t.contactInfo.businessHours.sunday
    }
  ]

  const features = language === 'en' ? [
    "Free Consultation",
    "Quick Response", 
    "Experienced Professional",
    "Quality Guarantee"
  ] : [
    "Ymgynghoriad Am Ddim",
    "Ymateb Cyflym", 
    "Proffesiynol Profiadol",
    "Gwarant Ansawdd"
  ]

  return (
    <section id="contact" className="section-padding bg-gradient-to-br from-white to-gray-50/15">
      <div className="container-sharp">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-section font-bold text-gray-900 mb-6 text-sharp">
            {t.title}
          </h2>
          <p className="text-large text-gray-600 max-w-3xl mx-auto text-sharp">
            {t.subtitle}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-8"
          >
            <div className="text-center lg:text-left">
              <h3 className="text-3xl font-bold text-gray-900 mb-6 text-sharp">
                {language === 'en' ? (
                  <>
                    Ready to Transform Your Garden?
                  </>
                ) : (
                  <>
                    Yn Barod i Trawsnewid Eich Gardd?
                  </>
                )}
              </h3>
              <p className="text-lg text-gray-600 mb-8 text-sharp">
                {language === 'en' 
                  ? "Get in touch today for a free consultation. I'm here to help bring your garden vision to life."
                  : "Cysylltwch â ni heddiw am ymgynghoriad am ddim. Rwyf yma i helpu dod â'ch gweledigaeth gardd i fywyd."
                }
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              {contactInfo.map((info, index) => {
                const IconComponent = info.icon
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                    className="flex items-start"
                  >
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-gray-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1 text-sharp">
                        {info.title}
                      </h4>
                      {info.link ? (
                        <a
                          href={info.link}
                          className="text-emerald-600 hover:text-emerald-700 transition-colors text-lg font-medium text-sharp"
                          target={info.value === (language === 'en' ? 'Barry and surrounding areas' : "Y Barri a'r ardaloedd cyfagos") ? '_blank' : undefined}
                          rel={info.value === (language === 'en' ? 'Barry and surrounding areas' : "Y Barri a'r ardaloedd cyfagos") ? 'noopener noreferrer' : undefined}
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-gray-600 text-lg font-medium text-sharp">{info.value}</p>
                      )}
                      <p className="text-sm text-gray-600 mt-1 text-sharp">{info.description}</p>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>

          {/* Why Choose Us */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Payment Methods Card */}
            <div className="rounded-3xl p-8 card-sharp flex flex-col items-center mb-4 bg-white">
              <h4 className="text-2xl font-bold text-gray-900 mb-4 text-sharp">{t.payment.title}</h4>
              <div className="flex gap-6 mb-4">
                <span className="flex flex-col items-center">
                  <span className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-1">
                    <Banknote className="w-8 h-8 text-gray-600" />
                  </span>
                </span>
                <span className="flex flex-col items-center">
                  <span className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-1">
                    <ArrowLeftRight className="w-8 h-8 text-gray-600" />
                  </span>
                </span>
                <span className="flex flex-col items-center">
                  <span className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-1">
                    <CreditCard className="w-8 h-8 text-gray-600" />
                  </span>
                </span>
              </div>
              <p className="text-gray-700 text-center text-base">{t.payment.description}</p>
            </div>

            {/* Why Choose Us */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-3xl p-8 card-sharp">
              <h4 className="text-2xl font-bold text-gray-900 mb-6 text-sharp">
                {language === 'en' ? 'Why Choose JC Gardening?' : 'Pam Dewis JC Gardening?'}
              </h4>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                    className="flex items-center"
                  >
                    <CheckCircle className="w-5 h-5 text-gray-600 mr-3 flex-shrink-0" />
                    <span className="text-gray-700 font-medium text-sharp">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 text-white text-center card-sharp">
              <h4 className="text-2xl font-bold mb-4 text-sharp text-white">
                {language === 'en' 
                  ? "Let's Start Your Garden's Transformation"
                  : "Gadewch i Ni Ddechrau Eich Trawsnewid Gardd"
                }
              </h4>
              <p className="text-gray-300 mb-6 text-sharp leading-relaxed">
                {language === 'en'
                  ? "Every great garden begins with a conversation. Click the phone number or email above to get started."
                  : "Mae pob gardd wych yn dechrau gyda sgwrs. Cliciwch ar y rhif ffôn neu e-bost uchod i ddechrau."
                }
              </p>
              <div className="text-emerald-400 font-medium text-sharp">
                {language === 'en' 
                  ? "Free consultation • No obligation • Professional service"
                  : "Ymgynghoriad am ddim • Dim rhwymedigaeth • Gwasanaeth proffesiynol"
                }
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
} 